import React from 'react';
import { getRoleName, formatDateTime } from '../../utils/helpers';
import { FiEdit2, FiTrash2, FiAtSign, FiUser, FiClock, FiCalendar, FiChevronUp, FiChevronDown, FiKey, FiEye, FiFolder } from 'react-icons/fi';
import clsx from 'clsx';
import { USER_ROLES } from '../../utils/constants';

const UserTable = ({ users, onEdit, onDelete, onChangePassword, onViewLoginHistory, currentUserId, currentUserRole, sortField, sortOrder, onSort }) => {
  const isPlatformAdmin = currentUserRole === USER_ROLES.PLATFORM_ADMIN;
  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;

    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
        onClick={() => onSort(field)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          <div className="flex flex-col">
            {isActive && sortOrder === 'asc' ? (
              <FiChevronUp className="h-4 w-4 text-blue-600" />
            ) : isActive && sortOrder === 'desc' ? (
              <FiChevronDown className="h-4 w-4 text-blue-600" />
            ) : (
              <div className="h-4 w-4 text-gray-300">
                <FiChevronUp className="h-3 w-3" />
              </div>
            )}
          </div>
        </div>
      </th>
    );
  };

  const isUserExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiUser className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No se encontraron usuarios
        </h3>
        <p className="text-gray-500">
          No hay usuarios que coincidan con los filtros actuales
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader field="name">Usuario</SortableHeader>
              <SortableHeader field="role">Rol</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <SortableHeader field="last_login">Último acceso</SortableHeader>
              {isPlatformAdmin && (
                <SortableHeader field="login_count">Total logins</SortableHeader>
              )}
              <SortableHeader field="expiration_date">Caducidad</SortableHeader>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        {user.id === currentUserId && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Tú
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        @{user.username}
                      </div>
                      {user.email && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <FiAtSign className="h-3 w-3 mr-1" />
                          {user.email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {getRoleName(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col space-y-1">
                    <span className={clsx(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit',
                      {
                        'bg-green-100 text-green-800': user.is_active && !isUserExpired(user.expiration_date),
                        'bg-red-100 text-red-800': !user.is_active,
                        'bg-orange-100 text-orange-800': user.is_active && isUserExpired(user.expiration_date),
                      }
                    )}>
                      {!user.is_active ? 'Inactivo' : isUserExpired(user.expiration_date) ? 'Caducado' : 'Activo'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? (
                    <div className="flex items-center space-x-1">
                      <FiClock className="h-4 w-4 text-gray-400" />
                      <span>
                        {formatDateTime(new Date(user.last_login))}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">Nunca</span>
                  )}
                </td>
                {isPlatformAdmin && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{user.login_count || 0}</span>
                      {user.login_count > 0 && (
                        <button
                          onClick={() => onViewLoginHistory(user)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                          title="Ver historial de accesos"
                        >
                          <FiEye className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.expiration_date ? (
                    <div className="flex items-center space-x-1">
                      <FiCalendar className={clsx(
                        'h-4 w-4',
                        isUserExpired(user.expiration_date) ? 'text-orange-500' : 'text-gray-400'
                      )} />
                      <span className={clsx(
                        isUserExpired(user.expiration_date) && 'text-orange-600 font-medium'
                      )}>
                        {formatDateTime(new Date(user.expiration_date))}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">Sin caducidad</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => onEdit(user)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50"
                      title="Editar usuario"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    {user.documents_url && (
                      <a
                        href={user.documents_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50"
                        title="Abrir carpeta de documentos"
                      >
                        <FiFolder className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      onClick={() => onChangePassword(user)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                      title="Cambiar contraseña"
                    >
                      <FiKey className="h-4 w-4" />
                    </button>
                    {user.id !== currentUserId && (
                      <button
                        onClick={() => onDelete(user)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                        title="Eliminar usuario"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;